/**
 * Drawer Search Functionality
 * Maneja la búsqueda predictiva dentro del drawer
 */

class DrawerSearch {
  constructor() {
    this.searchInput = null;
    this.searchResults = null;
    this.searchSuggestions = null;
    this.searchLoading = null;
    this.searchTimeout = null;
    this.init();
  }

  init() {
    // Escuchar cuando se abra el drawer de búsqueda
    document.addEventListener('openDrawer', (event) => {
      if (event.detail.drawerId === 'search') {
        this.initializeSearchElements();
        this.focusSearchInput();
      }
    });

    // Delegación de eventos para el formulario de búsqueda
    document.addEventListener('input', (event) => {
      if (event.target.matches('#search-input-drawer')) {
        this.handleSearchInput(event);
      }
    });

    document.addEventListener('submit', (event) => {
      if (event.target.matches('.search-form') && event.target.closest('[data-drawer-id="search"]')) {
        this.handleSearchSubmit(event);
      }
    });

    // Manejar click del botón remove
    document.addEventListener('click', (event) => {
      if (event.target.matches('#search-remove-btn')) {
        this.clearSearch();
      }
    });

    // Limpiar búsqueda cuando se cierre el drawer
    document.addEventListener('click', (event) => {
      if (event.target.closest('.js-drawer-close') && 
          event.target.closest('[data-drawer-id="search"]')) {
        this.clearSearch();
      }
    });
  }

  initializeSearchElements() {
    this.searchInput = document.getElementById('search-input-drawer');
    this.searchResults = document.getElementById('search-results');
    this.searchSuggestions = document.getElementById('search-suggestions');
    this.searchLoading = document.getElementById('search-loading');
    this.searchRemoveBtn = document.getElementById('search-remove-btn');
  }

  focusSearchInput() {
    // Pequeño delay para asegurar que el drawer esté completamente abierto
    setTimeout(() => {
      if (this.searchInput) {
        this.searchInput.focus();
      }
    }, 300);
  }

  handleSearchInput(event) {
    const query = event.target.value.trim();
    
    // Mostrar/ocultar botón remove basado en si hay texto
    this.toggleRemoveButton(query.length > 0);
    
    // Limpiar timeout anterior
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }

    if (query.length === 0) {
      this.showSuggestions();
      return;
    }

    if (query.length < 2) {
      return; // No buscar con menos de 2 caracteres
    }

    // Debounce la búsqueda
    this.searchTimeout = setTimeout(() => {
      this.performSearch(query);
    }, 300);
  }

  handleSearchSubmit(event) {
    event.preventDefault();
    const query = this.searchInput.value.trim();
    
    if (query.length > 0) {
      // Redirigir a la página de resultados de búsqueda
      window.location.href = `/search?q=${encodeURIComponent(query)}`;
    }
  }

  async performSearch(query) {
    this.showLoading();

    try {
      // Intentar primero con la API de sugerencias predictivas
      let response = await fetch(`/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product&resources[limit]=8`);
      
      if (!response.ok) {
        // Si falla, intentar con la API de búsqueda estándar
        console.log('Predictive search failed, trying standard search...');
        response = await fetch(`/search?q=${encodeURIComponent(query)}&view=json`);
        
        if (!response.ok) {
          // Si también falla, intentar una búsqueda más simple
          response = await fetch(`/products.json?limit=8`);
          
          if (!response.ok) {
            throw new Error('All search methods failed');
          }
          
          const data = await response.json();
          // Filtrar productos que contengan el término de búsqueda
          const filteredProducts = data.products.filter(product => 
            product.title.toLowerCase().includes(query.toLowerCase()) ||
            product.tags.some(tag => tag.toLowerCase().includes(query.toLowerCase()))
          );
          
          this.displaySearchResults({ resources: { results: { products: filteredProducts } } }, query);
          return;
        }
        
        // Manejar respuesta de búsqueda estándar
        const text = await response.text();
        console.log('Standard search response:', text);
        
        // Crear estructura compatible
        const mockData = { resources: { results: { products: [] } } };
        this.displaySearchResults(mockData, query);
        return;
      }

      const data = await response.json();
      console.log('Predictive search successful:', data);
      this.displaySearchResults(data, query);
      
    } catch (error) {
      console.error('Search error details:', error);
      this.showError();
    }
  }

  displaySearchResults(data, query) {
    if (!this.searchResults) return;

    // Debug: ver la estructura de los datos
    console.log('Search API response:', data);
    
    // Manejar diferentes estructuras de respuesta
    let products = [];
    
    if (data.resources && data.resources.results && data.resources.results.products) {
      products = data.resources.results.products;
    } else if (data.products) {
      products = data.products;
    } else if (Array.isArray(data)) {
      products = data;
    }
    
    console.log('Products found:', products);
    
    if (!products || products.length === 0) {
      this.showNoResults(query);
      return;
    }

    let resultsHTML = `
      <div class="search-results-header">
        <h3 class="search-section-title">(${products.length}) results</h3>
      </div>
      <div class="search-products">
    `;

    products.forEach(product => {
      // Obtener la URL de la imagen correctamente
      let imageUrl = '';
      if (product.featured_image) {
        // Si featured_image es un objeto, extraer la URL
        if (typeof product.featured_image === 'object' && product.featured_image.url) {
          imageUrl = product.featured_image.url;
        } else if (typeof product.featured_image === 'string') {
          imageUrl = product.featured_image;
        }
      } else if (product.image) {
        // Si image es un objeto, extraer la URL
        if (typeof product.image === 'object' && product.image.url) {
          imageUrl = product.image.url;
        } else if (typeof product.image === 'string') {
          imageUrl = product.image;
        }
      } else if (product.images && product.images.length > 0) {
        // Manejar tanto objetos imagen como URLs directas
        const firstImage = product.images[0];
        if (typeof firstImage === 'object' && firstImage.src) {
          imageUrl = firstImage.src;
        } else if (typeof firstImage === 'object' && firstImage.url) {
          imageUrl = firstImage.url;
        } else if (typeof firstImage === 'string') {
          imageUrl = firstImage;
        }
      }
      
      // Debug: ver qué imagen se está usando
      console.log('Product:', product.title, 'Image URL:', imageUrl);
      
      // Asegurar que la URL tenga el protocolo correcto (solo si es string)
      if (imageUrl && typeof imageUrl === 'string' && !imageUrl.startsWith('http')) {
        imageUrl = 'https:' + imageUrl;
      }
      
      // Obtener precio de manera más robusta
      let price = '';
      if (product.price) {
        price = this.formatMoney(product.price);
      } else if (product.variants && product.variants.length > 0 && product.variants[0].price) {
        price = this.formatMoney(product.variants[0].price);
      }
      
      // Obtener URL del producto
      let productUrl = product.url || `/products/${product.handle}` || '#';
      
      // Obtener descripción del producto
      let description = '';
      
      // Intentar diferentes campos de descripción (en orden de prioridad)
      if (product.body) {
        description = product.body.replace(/<[^>]*>/g, '').trim();
      } else if (product.description) {
        description = product.description.replace(/<[^>]*>/g, '').trim();
      } else if (product.body_html) {
        description = product.body_html.replace(/<[^>]*>/g, '').trim();
      } else if (product.excerpt) {
        description = product.excerpt.replace(/<[^>]*>/g, '').trim();
      } else if (product.summary) {
        description = product.summary.replace(/<[^>]*>/g, '').trim();
      } else if (product.content) {
        description = product.content.replace(/<[^>]*>/g, '').trim();
      }
      
      // Limitar longitud
      if (description && description.length > 100) {
        description = description.substring(0, 100) + '...';
      }
      
      // Debug: verificar descripción final
      console.log('Final description for', product.title, ':', description || 'No description available');

      resultsHTML += `
        <div class="search-product-item">
          <a href="${productUrl}" class="search-product-link">
            <div class="search-product-image">
              ${imageUrl ? `<img src="${imageUrl}" alt="${product.title}" loading="lazy" onerror="this.style.display='none'">` : '<div class="search-product-no-image"></div>'}
            </div>
            <div class="search-product-info">
              <h4 class="search-product-title heading-sm">${product.title}</h4>
              ${description ? `<p class="search-product-description body">${description}</p>` : ''}
              ${price ? `<span class="search-product-price body">${price}</span>` : ''}
            </div>
          </a>
        </div>
      `;
    });

    resultsHTML += '</div>';

    // Añadir botón fijo "See all search results"
    resultsHTML += `
      <div class="search-results-footer">
        <a href="/search?q=${encodeURIComponent(query)}" class="search-see-all-button">
          See all search results
        </a>
      </div>
    `;

    this.searchResults.innerHTML = resultsHTML;
    this.showResults();
  }

  showSuggestions() {
    if (this.searchSuggestions) this.searchSuggestions.style.display = 'block';
    if (this.searchResults) this.searchResults.style.display = 'none';
    if (this.searchLoading) this.searchLoading.style.display = 'none';
  }

  showResults() {
    if (this.searchSuggestions) this.searchSuggestions.style.display = 'none';
    if (this.searchResults) this.searchResults.style.display = 'block';
    if (this.searchLoading) this.searchLoading.style.display = 'none';
  }

  showLoading() {
    if (this.searchSuggestions) this.searchSuggestions.style.display = 'none';
    if (this.searchResults) this.searchResults.style.display = 'none';
    if (this.searchLoading) this.searchLoading.style.display = 'flex';
  }

  showNoResults(query) {
    if (!this.searchResults) return;

    this.searchResults.innerHTML = `
      <div class="search-no-results">
        <h3 class="search-section-title">No results found for "${query}"</h3>
        <p>Try other search terms</p>
      </div>
    `;
    
    this.showResults();
  }

  showError() {
    if (!this.searchResults) return;

    this.searchResults.innerHTML = `
      <div class="search-error">
        <h3 class="search-section-title">Search error</h3>
        <p>An error occurred. Please try again.</p>
      </div>
    `;
    
    this.showResults();
  }

  clearSearch() {
    if (this.searchInput) {
      this.searchInput.value = '';
    }
    this.toggleRemoveButton(false);
    this.showSuggestions();
  }

  toggleRemoveButton(show) {
    if (this.searchRemoveBtn) {
      this.searchRemoveBtn.style.display = show ? 'block' : 'none';
    }
  }

  formatMoney(cents) {
    // Función mejorada de formateo de dinero
    if (!cents && cents !== 0) return '';
    
    // Convertir a número si es string
    const amount = typeof cents === 'string' ? parseFloat(cents) : cents;
    
    // Si ya está en formato decimal (como 29.99), no dividir por 100
    const finalAmount = amount > 1000 ? amount / 100 : amount;
    
    try {
      return new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: 'USD' // Cambia según tu moneda
      }).format(finalAmount);
    } catch (error) {
      // Fallback simple si Intl no funciona
      return `$${finalAmount.toFixed(2)}`;
    }
  }
}

// Inicializar cuando el DOM esté listo
document.addEventListener('DOMContentLoaded', () => {
  new DrawerSearch();
});

// Estilos adicionales para los resultados de búsqueda
const searchStyles = `
  .search-results-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 16px;
    padding-bottom: 8px;
  }

  .search-view-all {
    font-size: 12px;
    color: var(--color-dark-1, #000);
    text-decoration: none;
    text-transform: uppercase;
  }

  .search-view-all:hover {
    text-decoration: underline;
  }

  .search-products {
    display: grid;
    grid-template-columns: 1fr;
    gap: 16px;
  }

  @media (min-width: 1440px) {
    .search-products {
      grid-template-columns: 1fr 1fr;
    }
  }

  .search-product-item {
    border-radius: 0px;
    overflow: hidden;
    transition: background-color 0.2s ease;
  }

  .search-product-item:hover {
    background: #ffffff;
  }

  .search-product-link {
    display: flex;
    gap: 12px;
    padding: 8px;
    text-decoration: none;
    color: var(--color-dark-1, #000);
    flex-direction: row;
  }

  @media (min-width: 1440px) {
    .search-product-link {
      flex-direction: column;
      gap: 8px;
    }
  }

  .search-product-image {
    width: 120px;
    aspect-ratio: 3/4;
    border-radius: 0px;
    overflow: hidden;
    background: var(--color-background-hover, #f5f5f5);
    flex-shrink: 0;
    position: relative;
  }

  @media (min-width: 1440px) {
    .search-product-image {
      width: 100%;
      flex-shrink: 1;
    }
  }

  .search-product-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    display: block;
    position: absolute;
    top: 0;
    left: 0;
    transition: transform 0.5s ease;
  }

  .search-product-item:hover .search-product-image img {
    transform: scale(1.02);
  }

  .search-product-no-image {
    width: 100%;
    height: 100%;
    background: var(--color-border, #e0e0e0);
    display: flex;
    align-items: center;
    justify-content: center;
    color: var(--color-text-muted, #666);
    font-size: 12px;
  }

  .search-product-no-image::after {
    content: "No image";
  }

  .search-product-info {
    flex: 1;
    display: flex;
    flex-direction: column;
    justify-content: center;
  }

  @media (min-width: 1440px) {
    .search-product-info {
      justify-content: flex-start;
      gap: 4px;
      padding-top: 8px;
    }
  }

  .search-product-title {
    margin: 0 0 4px 0;
  }

  .search-product-description {
    margin: 0 0 4px 0;
    color: var(--color-dark-1, #666);
    font-size: 14px;
    line-height: 1.4;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .search-product-price {
    font-size: 14px;
    color: var(--color-dark-1, #666);
  }

  .search-no-results,
  .search-error {
    text-align: center;
    padding: 40px 20px;
  }

  .search-no-results p,
  .search-error p {
    color: var(--color-dark-1, #666);
    margin-bottom: 20px;
  }

  .search-results-footer {
    position: fixed;
    bottom: 0;
    left: 0;
    right: 0;
    width: 100%;
    background: var(--color-dark-1, #000);
    z-index: 1000;
  }

  .search-see-all-button {
    display: block;
    width: 100%;
    height: 48px;
    padding: 0 16px;
    background: var(--color-dark-1, #000);
    color: white;
    text-align: center;
    text-decoration: none;
    font-size: var(--font-button-size);
    line-height: var(--font-button-line);
    font-weight: var(--font-button-weight);
    letter-spacing: var(--font-button-letter);
    font-family: var(--font-button-family);
    text-transform: var(--font-button-case);
    border: 1px solid var(--color-dark-1, #000);
    transition: all 0.2s ease;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .search-see-all-button:hover {
    background: var(--color-dark-2, #535353);
    border-color: var(--color-dark-2, #535353);
  }

  /* Agregar padding inferior al contenedor de resultados para evitar que el botón cubra contenido */
  .search-products {
    padding-bottom: 80px;
  }
`;

// Inyectar estilos
const styleSheet = document.createElement('style');
styleSheet.textContent = searchStyles;
document.head.appendChild(styleSheet);
